/**
 * Tenant Detail View
 * View and manage individual tenant configuration
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { tenantsService } from '../../services/api';
import SubscriptionTab from './SubscriptionTab';
import AddOnsTab from './AddOnsTab';
import TenantResetPanel from './TenantResetPanel';
import { formatDate, formatCurrency, formatNumber, getInitials } from '../../utils/formatters';

const USER_ROLES = [
  { value: 'tenant_admin', label: 'Tenant Administrator' },
  { value: 'admin', label: 'Administrator' },
  { value: 'staff', label: 'Staff' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'customer', label: 'Customer' },
];

const emptyUserForm = {
  email: '',
  password: '',
  name: '',
  role: 'staff',
  is_active: true,
};

const TenantDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  
  // Add / edit user modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [addUserForm, setAddUserForm] = useState(emptyUserForm);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState(null);

  // Settings edit state
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  useEffect(() => {
    loadTenantData();
  }, [id]);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      const [tenantData, usersData, statsData] = await Promise.all([
        tenantsService.getById(id).catch(() => null),
        tenantsService.getUsers(id).catch(() => ({ data: [] })),
        tenantsService.getStats(id).catch(() => null)
      ]);

      if (tenantData) {
        const t = tenantData.data || tenantData;
        setTenant(t);
        setSettingsForm({
          name:            t.name || '',
          description:     t.description || '',
          email:           t.email || '',
          phone:           t.phone || '',
          address:         t.address || '',
          city:            t.city || '',
          state:           t.state || '',
          zip_code:        t.zip_code || '',
          primary_color:   t.primary_color || '#2d5016',
          secondary_color: t.secondary_color || '#4a7c59',
          tax_rate:        t.tax_rate != null ? String(t.tax_rate) : '',
          timezone:        t.timezone || 'America/Chicago',
          currency:        t.currency || 'USD',
          is_active:       t.is_active !== false,
        });
      } else {
        // Demo fallback
        setTenant({
          id,
          name: 'Hood Family Farms',
          slug: 'hood-family-farms',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          settings: {
            business_name: 'Hood Family Farms',
            business_type: 'Farm',
            tax_rate: 8.25,
            timezone: 'America/Chicago'
          }
        });
      }

      setUsers(usersData.data || []);
      setStats(statsData?.data || {
        userCount: 1,
        transactionCount: 0,
        accountCount: 0,
        lastActivity: new Date().toISOString()
      });
    } catch (err) {
      console.error('Failed to load tenant:', err);
      setError('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const closeUserModal = () => {
    setShowAddUserModal(false);
    setEditingUserId(null);
    setAddUserForm(emptyUserForm);
    setAddUserError(null);
  };

  const openAddUser = () => {
    setEditingUserId(null);
    setAddUserForm(emptyUserForm);
    setAddUserError(null);
    setShowAddUserModal(true);
  };

  const openEditUser = (user) => {
    setEditingUserId(user.id);
    setAddUserForm({
      email: user.email || '',
      password: '',
      name: user.name || '',
      role: user.role || 'staff',
      is_active: user.is_active !== false,
    });
    setAddUserError(null);
    setShowAddUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setAddUserError(null);
    setAddUserLoading(true);

    try {
      if (editingUserId) {
        const payload = {
          email: addUserForm.email,
          name: addUserForm.name,
          role: addUserForm.role,
          is_active: addUserForm.is_active,
        };
        if (addUserForm.password) payload.password = addUserForm.password;
        await tenantsService.updateUser(id, editingUserId, payload);
      } else {
        await tenantsService.addUser(id, addUserForm);
      }
      closeUserModal();
      const usersData = await tenantsService.getUsers(id);
      setUsers(usersData.data || []);
    } catch (err) {
      console.error('Failed to save user:', err);
      setAddUserError(err.response?.data?.message || 'Failed to save user. Please try again.');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleSettingsSave = async () => {
    setSettingsSaving(true);
    setSettingsError(null);
    setSettingsSuccess(false);
    try {
      const payload = {
        ...settingsForm,
        tax_rate: settingsForm.tax_rate !== '' ? Number(settingsForm.tax_rate) : null,
      };
      const res = await tenantsService.update(id, payload);
      const updated = res.data || res;
      setTenant(updated);
      setSettingsSuccess(true);
      setTimeout(() => setSettingsSuccess(false), 3000);
    } catch (err) {
      setSettingsError(err.response?.data?.message || err.message || 'Failed to save settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const sf = (field, value) => setSettingsForm(f => ({ ...f, [field]: value }));

  const tabs = [
    { id: 'overview',      label: 'Overview',     icon: Icons.Home       },
    { id: 'users',         label: 'Users',        icon: Icons.Users      },
    { id: 'subscription',  label: 'Subscription', icon: Icons.CreditCard },
    { id: 'addons',        label: 'Add-ons',      icon: Icons.Package    },
    { id: 'settings',      label: 'Settings',     icon: Icons.Settings   },
    { id: 'integrations',  label: 'Integrations', icon: Icons.Link       },
  ];

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading tenant details...</p>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="empty-state">
        <Icons.AlertCircle size={64} />
        <h3>Tenant not found</h3>
        <p>The requested tenant could not be found.</p>
        <button className="btn btn-primary mt-2" onClick={() => navigate('/admin/tenants')}>
          Back to Tenants
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Tenant Header */}
      <div className="card mb-3">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div 
            className="tenant-avatar"
            style={{ 
              width: '64px', 
              height: '64px', 
              fontSize: '24px',
              background: tenant.primary_color || (tenant.is_active ? 'var(--accent-primary)' : 'var(--text-muted)')
            }}
          >
            {getInitials(tenant.name)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
              <h2 style={{ margin: 0, fontSize: '24px' }}>{tenant.name}</h2>
              <span className={`badge badge-${tenant.is_active ? 'success' : 'neutral'}`}>
                {tenant.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              ID: {tenant.id} • Created {formatDate(tenant.created_at)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/admin/tenants')}>
              <Icons.ChevronLeft size={18} />
              Back
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => {
                const backOfficeUrl = `https://${tenant.slug}.office.busmgr.com`;
                window.open(backOfficeUrl, '_blank');
              }}
            >
              <Icons.ExternalLink size={18} />
              Open Back Office
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '24px',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '0'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '-1px'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          {/* Stats */}
          <div className="stats-grid mb-3">
            <div className="stat-card">
              <div className="stat-icon primary"><Icons.Users size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(stats?.userCount || 0)}</div>
                <div className="stat-label">Users</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon success"><Icons.Activity size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(stats?.transactionCount || 0)}</div>
                <div className="stat-label">Transactions</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon warning"><Icons.BookOpen size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(stats?.accountCount || 0)}</div>
                <div className="stat-label">GL Accounts</div>
              </div>
            </div>
          </div>

          {/* Quick Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 className="card-title" style={{ margin: 0 }}>Business Information</h3>
                <button className="btn btn-secondary btn-sm" onClick={() => setActiveTab('settings')}>
                  <Icons.Edit size={14} /> Edit
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Business Name',  value: tenant.name },
                  { label: 'Email',          value: tenant.email },
                  { label: 'Phone',          value: tenant.phone },
                  { label: 'Address',        value: [tenant.address, tenant.city, tenant.state, tenant.zip_code].filter(Boolean).join(', ') },
                  { label: 'Tax Rate',       value: tenant.tax_rate != null ? tenant.tax_rate + '%' : null },
                  { label: 'Timezone',       value: tenant.timezone },
                  { label: 'Currency',       value: tenant.currency },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px' }}>{label}</div>
                    <div style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: value ? 'normal' : 'italic' }}>
                      {value || 'Not set'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="card-title mb-2">Integration Status</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.CreditCard size={18} />
                    <span>Stripe Connect</span>
                  </div>
                  {tenant.stripe_account_id ? (
                    <span className={`badge badge-${tenant.stripe_charges_enabled ? 'success' : 'warning'}`}>
                      {tenant.stripe_charges_enabled ? 'Active' : tenant.stripe_account_status || 'Pending'}
                    </span>
                  ) : (
                    <span className="badge badge-neutral">Not Connected</span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.Database size={18} />
                    <span>Plaid</span>
                  </div>
                  <span className="badge badge-neutral">Not Connected</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.DollarSign size={18} />
                    <span>Platform Subscription</span>
                  </div>
                  {tenant.stripe_subscription_id ? (
                    <span className={`badge badge-${tenant.subscription_status === 'active' ? 'success' : 'warning'}`}>
                      {tenant.subscription_status}
                    </span>
                  ) : (
                    <span className="badge badge-neutral">No Subscription</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Tenant Users</h3>
            <button className="btn btn-primary btn-sm" onClick={openAddUser}>
              <Icons.Plus size={16} />
              Add User
            </button>
          </div>

          {users.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px' }}>
              <Icons.Users size={48} />
              <h3>No users found</h3>
              <p>Add users to this tenant to grant access</p>
              <button className="btn btn-primary mt-2" onClick={openAddUser}>
                <Icons.Plus size={16} />
                Add First User
              </button>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="user-avatar" style={{ width: '32px', height: '32px', fontSize: '12px' }}>
                          {getInitials(user.name || user.email)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{user.name || 'No name'}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="badge badge-info">{user.role}</span></td>
                    <td>
                      <span className={`badge badge-${user.is_active ? 'success' : 'neutral'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{user.last_login ? formatDate(user.last_login) : 'Never'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditUser(user)}
                        aria-label={`Edit ${user.name || user.email}`}
                      >
                        <Icons.Edit size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'settings' && settingsForm && (
        <>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 className="card-title" style={{ margin: 0 }}>Tenant Settings</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {settingsSuccess && (
                <span style={{ color: 'var(--success, #22c55e)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icons.CheckCircle size={16} /> Saved
                </span>
              )}
              <button className="btn btn-primary" onClick={handleSettingsSave} disabled={settingsSaving}>
                {settingsSaving ? <><Icons.Loader size={16} className="spin" /> Saving…</> : <><Icons.Check size={16} /> Save Changes</>}
              </button>
            </div>
          </div>

          {settingsError && (
            <div className="alert alert-error mb-3" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Icons.AlertCircle size={16} /> {settingsError}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Business Name</label>
              <input className="form-control" value={settingsForm.name} onChange={e => sf('name', e.target.value)} />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Description</label>
              <input className="form-control" value={settingsForm.description} onChange={e => sf('description', e.target.value)} placeholder="Optional description" />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input className="form-control" type="email" value={settingsForm.email} onChange={e => sf('email', e.target.value)} placeholder="billing@example.com" />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-control" value={settingsForm.phone} onChange={e => sf('phone', e.target.value)} placeholder="e.g. 903-555-1234" />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Street Address</label>
              <input className="form-control" value={settingsForm.address} onChange={e => sf('address', e.target.value)} placeholder="123 Main St" />
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-control" value={settingsForm.city} onChange={e => sf('city', e.target.value)} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">State</label>
                <input className="form-control" value={settingsForm.state} onChange={e => sf('state', e.target.value)} placeholder="TX" maxLength={2} />
              </div>
              <div className="form-group">
                <label className="form-label">Zip Code</label>
                <input className="form-control" value={settingsForm.zip_code} onChange={e => sf('zip_code', e.target.value)} placeholder="75757" />
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '4px' }} />

            <div className="form-group">
              <label className="form-label">Primary Brand Color</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="color" value={settingsForm.primary_color} onChange={e => sf('primary_color', e.target.value)}
                  style={{ width: '48px', height: '38px', padding: '2px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer' }} />
                <input className="form-control" value={settingsForm.primary_color} onChange={e => sf('primary_color', e.target.value)} style={{ fontFamily: 'monospace' }} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Secondary Brand Color</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="color" value={settingsForm.secondary_color} onChange={e => sf('secondary_color', e.target.value)}
                  style={{ width: '48px', height: '38px', padding: '2px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer' }} />
                <input className="form-control" value={settingsForm.secondary_color} onChange={e => sf('secondary_color', e.target.value)} style={{ fontFamily: 'monospace' }} />
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '4px' }} />

            <div className="form-group">
              <label className="form-label">Tax Rate (%)</label>
              <input className="form-control" type="number" min="0" max="100" step="0.001"
                value={settingsForm.tax_rate} onChange={e => sf('tax_rate', e.target.value)} placeholder="e.g. 8.25" />
            </div>

            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select className="form-control" value={settingsForm.timezone} onChange={e => sf('timezone', e.target.value)}>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-control" value={settingsForm.currency} onChange={e => sf('currency', e.target.value)}>
                <option value="USD">USD — US Dollar</option>
                <option value="CAD">CAD — Canadian Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — British Pound</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Account Status</label>
              <select className="form-control" value={settingsForm.is_active ? 'active' : 'inactive'}
                onChange={e => sf('is_active', e.target.value === 'active')}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

          </div>
          <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
        <TenantResetPanel tenant={tenant} onReset={loadTenantData} />
      </>
      )}

      {activeTab === 'subscription' && (
        <SubscriptionTab tenant={tenant} />
      )}

      {activeTab === 'addons' && (
        <AddOnsTab tenant={tenant} />
      )}

      {activeTab === 'integrations' && (
        <div className="card">
          <h3 className="card-title mb-3">Integrations</h3>
          <p style={{ color: 'var(--text-muted)' }}>
            Integration management coming soon.
          </p>
        </div>
      )}

      {/* Add / edit user modal */}
      {showAddUserModal && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>{editingUserId ? 'Edit User' : `Add User to ${tenant.name}`}</h3>
              <button className="btn-close" onClick={closeUserModal}>
                <Icons.X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSaveUser}>
              <div className="modal-body">
                {addUserError && (
                  <div className="alert alert-error mb-2">
                    <Icons.AlertCircle size={18} />
                    {addUserError}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addUserForm.name}
                    onChange={e => setAddUserForm({ ...addUserForm, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={addUserForm.email}
                    onChange={e => setAddUserForm({ ...addUserForm, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{editingUserId ? 'New password' : 'Password *'}</label>
                  <input
                    type="password"
                    className="form-control"
                    value={addUserForm.password}
                    onChange={e => setAddUserForm({ ...addUserForm, password: e.target.value })}
                    placeholder={editingUserId ? 'Leave blank to keep current password' : 'Minimum 8 characters'}
                    minLength={addUserForm.password || !editingUserId ? 8 : undefined}
                    required={!editingUserId}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-control"
                    value={addUserForm.role}
                    onChange={e => setAddUserForm({ ...addUserForm, role: e.target.value })}
                  >
                    {USER_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <p className="form-hint" style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                    New Tenant always creates the first user as Tenant Administrator. Change it here if needed.
                  </p>
                </div>

                {editingUserId && (
                  <div className="form-group">
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={!!addUserForm.is_active}
                        onChange={e => setAddUserForm({ ...addUserForm, is_active: e.target.checked })}
                      />
                      Active
                    </label>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeUserModal}
                  disabled={addUserLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={addUserLoading}
                >
                  {addUserLoading ? (
                    <>
                      <Icons.Loader size={16} className="spin" />
                      Saving...
                    </>
                  ) : editingUserId ? (
                    <>
                      <Icons.Check size={16} />
                      Save
                    </>
                  ) : (
                    <>
                      <Icons.Plus size={16} />
                      Add User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDetailView;
